<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { t } from './i18n';
import { CURVE_VERSION, normalizeBadge } from './config';
import { ensureItemFonts, loadFontCatalog } from './fontEngine';
import { fitItemPatch } from './engine';
import { typographyPolicy } from './policy';
import type { TypographyBadgeConfig, TypographyDesignItem, TypographyShadowLayer } from './types';
import ProField from './ProField.vue';
import ProPanelSection from './ProPanelSection.vue';
import BrandColorField from './BrandColorField.vue';

const props = defineProps<{ item: TypographyDesignItem | null; update: (patch: Record<string, unknown>) => void }>();
const ready = ref(false);
const loading = ref(false);
const fonts = ref<string[]>([]);
const badge = computed(() => normalizeBadge(props.item?.typographyBadge));
const sizeLabel = computed(() => `${Math.round(Number(props.item?.width ?? 0))} × ${Math.round(Number(props.item?.height ?? 0))} px`);
const fontOptions = computed(() => {
  const current = [badge.value.top.fontFamily, badge.value.bottom.fontFamily, badge.value.center.fontFamily];
  const catalog = [...new Set([...fonts.value, ...current])];
  const allowed = typographyPolicy.value.allowedFonts;
  return catalog.filter((font) => !allowed.length || allowed.includes(font)).sort((a, b) => a.localeCompare(b));
});
const sharedFont = computed(() => {
  const values = [badge.value.top.fontFamily, badge.value.bottom.fontFamily, ...(badge.value.center.enabled ? [badge.value.center.fontFamily] : [])];
  return values.every((font) => font === values[0]) ? values[0] : '';
});
const sharedColor = computed(() => {
  const values = [badge.value.top.color, badge.value.bottom.color, ...(badge.value.center.enabled ? [badge.value.center.color] : [])];
  return values.every((color) => color === values[0]) ? values[0] : badge.value.top.color;
});

onMounted(async () => { fonts.value = (await loadFontCatalog()).map((font) => font.title).filter(Boolean); });
watch(() => [props.item?.id, props.item?.typographyBadge], async () => {
  if (!props.item) return;
  loading.value = true;
  ready.value = await ensureItemFonts(props.item);
  loading.value = false;
}, { immediate: true, deep: true });

function commit(next: TypographyBadgeConfig) {
  const normalized = normalizeBadge(next);
  const effects = typographyPolicy.value.allowedEffects;
  const maxChars = typographyPolicy.value.maxChars;
  normalized.top.text = normalized.top.text.slice(0, maxChars);
  normalized.bottom.text = normalized.bottom.text.slice(0, maxChars);
  normalized.center.text = normalized.center.text.slice(0, maxChars);
  if (!effects.includes('gradient') && (normalized.appearance.fill.type === 'linear-gradient' || normalized.appearance.fill.type === 'radial-gradient')) normalized.appearance.fill.type = 'solid';
  if (!effects.includes('outline')) {
    normalized.appearance.stroke.enabled = false;
    if (normalized.appearance.fill.type === 'none') normalized.appearance.fill.type = 'solid';
  }
  if (!effects.includes('shadow')) normalized.appearance.shadow.enabled = false;
  if (!effects.includes('extrude')) normalized.appearance.extrude.enabled = false;
  if (!props.item) return;
  props.update(fitItemPatch(props.item, { typographyBadge: normalized, typographyFontReady: ready.value }));
}
function setRegion(region: 'top' | 'bottom' | 'center', patch: Record<string, unknown>) { commit({ ...badge.value, [region]: { ...badge.value[region], ...patch } }); }
function setAllRegions(patch: Record<string, unknown>) {
  commit({ ...badge.value, top: { ...badge.value.top, ...patch }, bottom: { ...badge.value.bottom, ...patch }, center: { ...badge.value.center, ...patch } });
}
function setGeometry(patch: Partial<TypographyBadgeConfig['geometry']>) { commit({ ...badge.value, geometry: { ...badge.value.geometry, ...patch, curveVersion: CURVE_VERSION } }); }
function setAutoFit(patch: Partial<TypographyBadgeConfig['geometry']['autoFit']>) { setGeometry({ autoFit: { ...badge.value.geometry.autoFit, ...patch } }); }
function setSeparator(patch: Partial<TypographyBadgeConfig['separator']>) { commit({ ...badge.value, separator: { ...badge.value.separator, ...patch } }); }
function setAppearance<K extends keyof TypographyBadgeConfig['appearance']>(key: K, patch: Partial<TypographyBadgeConfig['appearance'][K]>) { commit({ ...badge.value, appearance: { ...badge.value.appearance, [key]: { ...badge.value.appearance[key], ...patch } } }); }
function gradientColor(index: number, color: string) { const colors = [...badge.value.appearance.fill.colors]; colors[index] = color; setAppearance('fill', { colors }); }
function shadowLayerPatch(patch: Partial<TypographyShadowLayer>) {
  const layers = [...badge.value.appearance.shadow.layers];
  layers[0] = { ...layers[0], ...patch };
  setAppearance('shadow', { layers });
}
const value = (event: Event) => (event.target as HTMLInputElement).value;
const number = (event: Event) => Number(value(event));
const checked = (event: Event) => (event.target as HTMLInputElement).checked;
</script>

<template>
  <section class="vptw:flex vptw:min-w-0 vptw:flex-col vptw:gap-3 vptw:text-(--vs-ink)">
    <header class="vptw:rounded-2xl vptw:border vptw:border-(--vs-accent) vptw:p-3" style="background:linear-gradient(135deg,var(--vs-accent-soft) 0%,var(--vs-surface) 75%)">
      <div class="vptw:flex vptw:min-w-0 vptw:items-start vptw:justify-between vptw:gap-2">
        <div class="vptw:min-w-0">
          <p class="vptw:m-0 vptw:text-sm vptw:font-semibold vptw:text-(--vs-ink)">{{ t('Smart Badge') }}</p>
          <p class="vptw:m-0 vptw:mt-1 vptw:text-[11px] vptw:leading-4 vptw:text-(--vs-ink-2)">{{ t('Edit every word while the badge stays balanced.') }}</p>
        </div>
        <span aria-live="polite" class="vptw:shrink-0 vptw:rounded-full vptw:px-2 vptw:py-1 vptw:text-[10px] vptw:font-semibold" :class="ready ? 'vptw:bg-(--vs-ok)/15 vptw:text-(--vs-ok)' : 'vptw:bg-(--vs-warn)/15 vptw:text-(--vs-warn)'">{{ loading ? t('Preparing…') : ready ? t('Ready for print') : t('Font needs attention') }}</span>
      </div>
      <p class="vptw:m-0 vptw:mt-2 vptw:text-[10px] vptw:text-(--vs-ink-3)">
        {{ t('Design size') }} · {{ sizeLabel }}
      </p>
    </header>

    <p v-if="!ready && !loading" role="status" class="vptw:m-0 vptw:rounded-xl vptw:border vptw:border-(--vs-warn)/30 vptw:bg-(--vs-warn)/15 vptw:p-3 vptw:text-xs vptw:leading-5 vptw:text-(--vs-warn)">
      {{ t('One or more badge fonts cannot be prepared for print. Choose another font.') }}
    </p>

    <fieldset class="vptw:m-0 vptw:min-w-0 vptw:overflow-hidden vptw:rounded-2xl vptw:border vptw:border-(--vs-line) vptw:bg-(--vs-surface) vptw:p-0 vptw:shadow-sm" :disabled="!ready">
      <ProPanelSection :title="t('Badge text')" :hint="t('Change the words customers see')" open>
        <div class="vptw:space-y-3">
          <ProField :label="t('Top text')">
            <input :aria-label="t('Top text')" :maxlength="typographyPolicy.maxChars" :value="badge.top.text" @input="setRegion('top', { text: value($event) })">
          </ProField>
          <ProField :label="t('Center text')" group>
            <div class="vptw:flex vptw:items-center vptw:gap-2">
              <input :aria-label="t('Center text')" class="vptw:min-w-0 vptw:flex-1" :disabled="!badge.center.enabled" :maxlength="typographyPolicy.maxChars" :value="badge.center.text" @input="setRegion('center', { text: value($event) })">
              <label class="vptw:flex vptw:shrink-0 vptw:items-center vptw:gap-1 vptw:text-[10px]">
                <input type="checkbox" :checked="badge.center.enabled" @change="setRegion('center', { enabled: checked($event) })">{{ t('Show') }}
              </label>
            </div>
          </ProField>
          <ProField :label="t('Bottom text')">
            <input :aria-label="t('Bottom text')" :maxlength="typographyPolicy.maxChars" :value="badge.bottom.text" @input="setRegion('bottom', { text: value($event) })">
          </ProField>
          <div class="vptw:grid vptw:grid-cols-2 vptw:gap-3 vptw:rounded-xl vptw:bg-(--vs-surface-2) vptw:p-3">
            <ProField :label="t('Font for all text')">
              <select :value="sharedFont" @change="setAllRegions({ fontFamily: value($event) })">
                <option v-if="!sharedFont" value="" disabled>{{ t('Mixed fonts') }}</option>
                <option v-for="font in fontOptions" :key="font" :value="font">{{ font }}</option>
              </select>
            </ProField>
            <ProField :label="t('Color for all text')" :hint="sharedColor">
              <BrandColorField :label="t('Color for all text')" :model-value="sharedColor" @update:model-value="setAllRegions({ color: $event })" />
            </ProField>
          </div>
        </div>
      </ProPanelSection>

      <ProPanelSection :title="t('Badge shape')" :hint="t('Choose the overall look')" open>
        <div class="vptw:grid vptw:grid-cols-2 vptw:gap-3">
          <ProField :label="t('Shape')">
            <select :value="badge.geometry.shape" @change="setGeometry({ shape: value($event) as 'circle' | 'ellipse' })">
              <option value="circle">{{ t('Circle') }}</option>
              <option value="ellipse">{{ t('Ellipse') }}</option>
            </select>
          </ProField>
          <ProField :label="t('Divider')">
            <select :value="badge.separator.kind" @change="setSeparator({ kind: value($event) as TypographyBadgeConfig['separator']['kind'] })">
              <option value="none">{{ t('None') }}</option>
              <option value="dot">{{ t('Dot') }}</option>
              <option value="star">{{ t('Star') }}</option>
              <option value="diamond">{{ t('Diamond') }}</option>
            </select>
          </ProField>
        </div>
      </ProPanelSection>

      <ProPanelSection :title="t('Color & effects')" :hint="t('Make the badge stand out')">
        <div class="vptw:space-y-2 vptw:text-xs">
          <label v-if="typographyPolicy.allowedEffects.includes('gradient')" class="vptw:flex vptw:items-center vptw:justify-between vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:p-3">
            <span class="vptw:font-semibold">{{ t('Gradient') }}</span>
            <input type="checkbox" :checked="badge.appearance.fill.type === 'linear-gradient'" @change="setAppearance('fill', { type: checked($event) ? 'linear-gradient' : 'solid' })">
          </label>
          <label v-for="effect in (['stroke', 'shadow', 'extrude'] as const)" v-show="typographyPolicy.allowedEffects.includes(effect === 'stroke' ? 'outline' : effect)" :key="effect" class="vptw:flex vptw:items-center vptw:justify-between vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:p-3">
            <span class="vptw:font-semibold">{{ effect === 'stroke' ? t('Outline') : effect === 'shadow' ? t('Shadow') : t('Depth') }}</span>
            <input type="checkbox" :checked="badge.appearance[effect].enabled" @change="setAppearance(effect, { enabled: checked($event) })">
          </label>
        </div>
      </ProPanelSection>

      <ProPanelSection :title="t('Advanced')" :hint="t('Fine-tune each region and shape')">
        <div class="vptw:space-y-4">
          <div v-for="region in (['top', 'bottom', 'center'] as const)" v-show="region !== 'center' || badge.center.enabled" :key="region" class="vptw:rounded-xl vptw:bg-(--vs-surface-2) vptw:p-3">
            <p class="vptw:m-0 vptw:mb-3 vptw:text-xs vptw:font-semibold vptw:capitalize">{{ t(region) }}</p>
            <div class="vptw:grid vptw:grid-cols-2 vptw:gap-3">
              <ProField class="vptw:col-span-2" :label="t('Font')">
                <select :value="badge[region].fontFamily" @change="setRegion(region, { fontFamily: value($event) })">
                  <option v-for="font in fontOptions" :key="font" :value="font">{{ font }}</option>
                </select>
              </ProField>
              <ProField :label="t('Weight')">
                <select :value="badge[region].fontWeight" @change="setRegion(region, { fontWeight: value($event) })">
                  <option value="400">{{ t('Regular') }}</option>
                  <option value="700">{{ t('Bold') }}</option>
                  <option value="900">{{ t('Black') }}</option>
                </select>
              </ProField>
              <ProField :label="t('Color')">
                <BrandColorField :label="t('Color')" :model-value="badge[region].color" @update:model-value="setRegion(region, { color: $event })" />
              </ProField>
              <ProField :label="t('Size')">
                <input type="number" min="4" max="300" :value="badge[region].fontSize" @change="setRegion(region, { fontSize: number($event) })">
              </ProField>
              <ProField :label="t('Spacing')">
                <input type="number" min="-20" max="100" :value="badge[region].spacing" @change="setRegion(region, { spacing: number($event) })">
              </ProField>
            </div>
          </div>

          <div class="vptw:grid vptw:grid-cols-2 vptw:gap-3">
            <ProField :label="t('Top side')">
              <select :value="badge.geometry.topSide" @change="setGeometry({ topSide: value($event) as 'outside' | 'inside' })">
                <option value="outside">{{ t('Outside') }}</option>
                <option value="inside">{{ t('Inside') }}</option>
              </select>
            </ProField>
            <ProField :label="t('Bottom side')">
              <select :value="badge.geometry.bottomSide" @change="setGeometry({ bottomSide: value($event) as 'outside' | 'inside' })">
                <option value="outside">{{ t('Outside') }}</option>
                <option value="inside">{{ t('Inside') }}</option>
              </select>
            </ProField>
            <ProField class="vptw:col-span-2" :label="t('Top curve')" :hint="`${badge.geometry.topSweep}°`">
              <input type="range" min="30" max="180" :value="badge.geometry.topSweep" @input="setGeometry({ topSweep: number($event) })">
            </ProField>
            <ProField class="vptw:col-span-2" :label="t('Bottom curve')" :hint="`${badge.geometry.bottomSweep}°`">
              <input type="range" min="30" max="180" :value="badge.geometry.bottomSweep" @input="setGeometry({ bottomSweep: number($event) })">
            </ProField>
            <ProField :label="t('Padding')">
              <input type="number" min="0" max="100" :value="badge.geometry.padding" @change="setGeometry({ padding: number($event) })">
            </ProField>
            <ProField :label="t('Divider size')">
              <input type="number" min="2" max="40" :value="badge.separator.size" @change="setSeparator({ size: number($event) })">
            </ProField>
          </div>

          <div class="vptw:rounded-xl vptw:bg-(--vs-surface-2) vptw:p-3 vptw:text-xs">
            <label class="vptw:flex vptw:items-center vptw:justify-between">
              <span>
                <b>{{ t('Auto fit') }}</b>
                <small class="vptw:block vptw:text-[10px] vptw:text-(--vs-ink-3)">{{ t('Keep text inside the badge') }}</small>
              </span>
              <input type="checkbox" :checked="badge.geometry.autoFit.enabled" @change="setAutoFit({ enabled: checked($event) })">
            </label>
            <div class="vptw:mt-3 vptw:grid vptw:grid-cols-2 vptw:gap-3">
              <ProField :label="t('Minimum size')">
                <input type="number" min="4" max="300" :value="badge.geometry.autoFit.minSize" @change="setAutoFit({ minSize: number($event) })">
              </ProField>
              <ProField :label="t('Maximum size')">
                <input type="number" min="4" max="300" :value="badge.geometry.autoFit.maxSize" @change="setAutoFit({ maxSize: number($event) })">
              </ProField>
            </div>
          </div>

          <div v-if="badge.appearance.fill.type === 'linear-gradient'" class="vptw:grid vptw:grid-cols-3 vptw:gap-2">
            <ProField :label="t('First color')">
              <BrandColorField :label="t('First color')" :model-value="badge.appearance.fill.colors[0]" @update:model-value="gradientColor(0, $event)" />
            </ProField>
            <ProField :label="t('Second color')">
              <BrandColorField :label="t('Second color')" :model-value="badge.appearance.fill.colors[1]" @update:model-value="gradientColor(1, $event)" />
            </ProField>
            <ProField :label="t('Angle')">
              <input type="number" min="-360" max="360" :value="badge.appearance.fill.angle" @change="setAppearance('fill', { angle: number($event) })">
            </ProField>
          </div>
          <div v-if="badge.appearance.stroke.enabled" class="vptw:grid vptw:grid-cols-2 vptw:gap-3">
            <ProField :label="t('Outline color')">
              <BrandColorField :label="t('Outline color')" :model-value="badge.appearance.stroke.color" @update:model-value="setAppearance('stroke', { color: $event })" />
            </ProField>
            <ProField :label="t('Outline width')">
              <input type="number" min="0" max="30" :value="badge.appearance.stroke.width" @change="setAppearance('stroke', { width: number($event) })">
            </ProField>
          </div>
          <div v-if="badge.appearance.shadow.enabled" class="vptw:grid vptw:grid-cols-3 vptw:gap-2">
            <ProField :label="t('Shadow color')">
              <BrandColorField :label="t('Shadow color')" :model-value="badge.appearance.shadow.layers[0].color" @update:model-value="shadowLayerPatch({ color: $event })" />
            </ProField>
            <ProField :label="t('X')">
              <input type="number" min="-100" max="100" :value="badge.appearance.shadow.layers[0].x" @change="shadowLayerPatch({ x: number($event) })">
            </ProField>
            <ProField :label="t('Y')">
              <input type="number" min="-100" max="100" :value="badge.appearance.shadow.layers[0].y" @change="shadowLayerPatch({ y: number($event) })">
            </ProField>
            <ProField class="vptw:col-span-3" :label="t('Blur')" :hint="String(badge.appearance.shadow.layers[0].blur)">
              <input type="range" min="0" max="40" :value="badge.appearance.shadow.layers[0].blur" @input="shadowLayerPatch({ blur: number($event) })">
            </ProField>
          </div>
          <div v-if="badge.appearance.extrude.enabled" class="vptw:grid vptw:grid-cols-3 vptw:gap-2">
            <ProField :label="t('Depth color')">
              <BrandColorField :label="t('Depth color')" :model-value="badge.appearance.extrude.color" @update:model-value="setAppearance('extrude', { color: $event })" />
            </ProField>
            <ProField :label="t('Depth')">
              <input type="number" min="0" max="100" :value="badge.appearance.extrude.depth" @change="setAppearance('extrude', { depth: number($event) })">
            </ProField>
            <ProField :label="t('Angle')">
              <input type="number" min="-360" max="360" :value="badge.appearance.extrude.angle" @change="setAppearance('extrude', { angle: number($event) })">
            </ProField>
          </div>
        </div>
      </ProPanelSection>
    </fieldset>
  </section>
</template>

<style scoped>
section :deep(input[type="text"]), section :deep(input:not([type])), section :deep(input[type="number"]), section :deep(select) {
  box-sizing: border-box; min-width: 0; width: 100%; border: 1px solid var(--vs-line); border-radius: .5rem; padding: .5rem .6rem; background: var(--vs-surface); color: var(--vs-ink);
}
section :deep(input[type="range"]) { width: 100%; accent-color: var(--vs-accent, #007aff); }
section :deep(input[type="checkbox"]) { accent-color: var(--vs-accent, #007aff); }
</style>
